import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { isAxiosError } from 'axios';

import { ReactComponent as ScanIcon } from 'assets/images/icons/scan.svg';
import { ReactComponent as ArrowDown } from 'assets/images/icons/arrow-list.svg';
import { ReactComponent as CloseIcon } from 'assets/images/icons/close.svg';

import useClickOutside from 'hooks/useClickOutside';

import Modal from 'ui/Modal';

import { showToast } from 'store/Toast/Toast.action';

import {
  GATEWAY_EUI_ALLOWED_TYPES,
  GATEWAY_EUI_MAX_SIZE,
  GATEWAY_MODELS,
  GatewayConflictDetail,
  GatewayModel,
  GatewayResponse,
  ObjectOption,
  UserOption,
  gatewaysApi,
} from 'services/gateways';

import { Gateway } from 'views/Gateways/Gateways';

import styles from 'assets/scss/modules/Gateways/Gateways.module.scss';

export type GatewayFormMode = 'create' | 'edit';

interface Props {
  mode: GatewayFormMode | null;
  gateway?: Gateway | null;
  onClose: () => void;
  onSuccess?: () => void;
  onCreated?: (created: GatewayResponse) => void;
}

const EUI_REGEX = /^[A-Za-z0-9]+$/;
const EUI_MAX = 16;

interface FormValues {
  name: string;
  userId: number | '';
  objectId: number | '';
  model: GatewayModel | '';
  eui: string;
}

const EMPTY: FormValues = {
  name: '',
  userId: '',
  objectId: '',
  model: '',
  eui: '',
};

const validationSchema = Yup.object<FormValues>({
  name: Yup.string()
    .test('required', 'Please fill out this field.', (v) => !!v && v.trim().length > 0),
  userId: Yup.mixed().test(
    'required',
    'Please choose an option.',
    (v) => typeof v === 'number',
  ),
  objectId: Yup.mixed().nullable(),
  model: Yup.string().test(
    'required',
    'Please choose an option.',
    (v) => !!v && (GATEWAY_MODELS as readonly string[]).includes(v),
  ),
  eui: Yup.string()
    .test('required', 'Please fill out this field.', (v) => !!v && v.trim().length > 0)
    .test('format', 'Gateway EUI must contain only Latin letters and digits.', (v) =>
      !v || EUI_REGEX.test(v.trim()),
    ),
});

interface TooltipProps {
  text: string;
  children: React.ReactNode;
}

const Tooltip: React.FC<TooltipProps> = ({ text, children }) => (
  <span className={styles.gwTooltipWrap}>
    {children}
    <span className={styles.gwTooltipBubble} role="tooltip">
      {text}
    </span>
  </span>
);

interface FieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  disabled?: boolean;
  invalid?: boolean;
  onClick?: () => void;
  className?: string;
}

const Field: React.FC<FieldProps> = ({
  label,
  required,
  children,
  disabled,
  invalid,
  onClick,
  className,
}) => (
  <div
    className={classNames(
      styles.gwField,
      { [styles.gwFieldDisabled]: disabled, [styles.gwFieldInvalid]: invalid },
      className,
    )}
    onClick={disabled ? undefined : onClick}
  >
    <span className={styles.gwFieldLabel}>
      {label}
      {required && <span className={styles.gwFieldRequired}> *</span>}
    </span>
    <div className={styles.gwFieldRow}>{children}</div>
  </div>
);

interface SelectOption<T> {
  value: T;
  label: string;
}

interface SelectFieldProps<T extends string | number> {
  label: string;
  required?: boolean;
  value: T | '';
  options: SelectOption<T>[];
  placeholder: string;
  searchPlaceholder?: string;
  searchable?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  onChange: (value: T) => void;
  onClear?: () => void;
}

function SelectField<T extends string | number>({
  label,
  required,
  value,
  options,
  placeholder,
  searchPlaceholder = 'Search...',
  searchable = false,
  disabled,
  invalid,
  onChange,
  onClear,
}: SelectFieldProps<T>) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  useClickOutside(wrapRef, () => {
    setOpen(false);
    setSearch('');
  });

  useEffect(() => {
    if (open && searchable) {
      searchRef.current?.focus();
    }
    if (!open) {
      setSearch('');
    }
  }, [open, searchable]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return options;
    return options.filter((o) => o.label.toLowerCase().includes(needle));
  }, [options, search]);

  const handlePick = (opt: SelectOption<T>) => {
    onChange(opt.value);
    setOpen(false);
    setSearch('');
  };

  const selectedLabel = options.find((o) => o.value === value)?.label || '';

  const inlineSearch = searchable && !disabled;
  const inputValue = inlineSearch && open ? search : selectedLabel;
  const inputPlaceholder =
    inlineSearch && open && selectedLabel
      ? searchPlaceholder
      : placeholder;

  return (
    <div ref={wrapRef} className={styles.gwSelectWrap}>
      <Field
        label={label}
        required={required}
        disabled={disabled}
        invalid={invalid}
        className={styles.gwSelectField}
        onClick={inlineSearch ? () => setOpen(true) : () => setOpen((o) => !o)}
      >
        {inlineSearch ? (
          <input
            ref={searchRef}
            type="text"
            className={styles.gwInput}
            value={inputValue}
            placeholder={inputPlaceholder}
            disabled={disabled}
            autoComplete="off"
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
          />
        ) : (
          <span
            className={classNames(styles.gwSelectValue, {
              [styles.gwSelectValueEmpty]: !value,
            })}
          >
            {selectedLabel || placeholder}
          </span>
        )}
        {onClear && value !== '' && !disabled && (
          <button
            type="button"
            className={styles.gwSelectClear}
            aria-label="Clear selection"
            onClick={(e) => {
              e.stopPropagation();
              onClear();
              setOpen(false);
              setSearch('');
            }}
          >
            <CloseIcon />
          </button>
        )}
        <ArrowDown
          className={classNames(styles.gwChevron, { [styles.gwChevronOpen]: open })}
        />
      </Field>

      {open && !disabled && (
        <div className={styles.gwDropdown}>
          <div className={styles.gwDropdownList}>
            {filtered.length === 0 ? (
              <div className={styles.gwOption}>No matches</div>
            ) : (
              filtered.map((opt) => (
                <div
                  key={String(opt.value)}
                  className={classNames(styles.gwOption, {
                    [styles.gwOptionActive]: opt.value === value,
                  })}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handlePick(opt)}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export const GatewayFormModal: React.FC<Props> = ({ mode, gateway, onClose, onSuccess, onCreated }) => {
  const isOpen = mode !== null;
  const isEdit = mode === 'edit';

  const dispatch = useDispatch();
  const [users, setUsers] = useState<UserOption[]>([]);
  const [objects, setObjects] = useState<ObjectOption[]>([]);
  const [euiRecognitionError, setEuiRecognitionError] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const formik = useFormik<FormValues>({
    initialValues: EMPTY,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, helpers) => {
      const trimmedName = values.name.trim();
      const objectIdValue = values.objectId === '' ? null : (values.objectId as number);

      try {
        if (isEdit && gateway) {
          await gatewaysApi.update(gateway.id, {
            name: trimmedName,
            user_id: values.userId as number,
            object_id: objectIdValue,
          });
          dispatch(showToast('The gateway was successfully updated.', 'success'));
        } else {
          const created = await gatewaysApi.create({
            name: trimmedName,
            eui: values.eui.trim().toUpperCase(),
            model: values.model as GatewayModel,
            freq_band: 'EU868',
            user_id: values.userId as number,
            object_id: objectIdValue,
          });
          dispatch(showToast('The gateway was successfully added.', 'success'));
          onCreated?.(created);
        }
        onSuccess?.();
        onClose();
      } catch (e) {
        const conflictBody = isAxiosError(e) ? e.response?.data : undefined;
        const conflict: GatewayConflictDetail[] | undefined = Array.isArray(conflictBody)
          ? (conflictBody as GatewayConflictDetail[])
          : Array.isArray((conflictBody as { detail?: unknown })?.detail)
            ? ((conflictBody as { detail: GatewayConflictDetail[] }).detail)
            : undefined;
        const euiConflict = conflict?.find((c) => c?.field === 'eui');

        if (!isEdit && euiConflict?.detail) {
          helpers.setFieldError('eui', euiConflict.detail);
          helpers.setFieldTouched('eui', true, false);
        } else {
          dispatch(
            showToast(
              isEdit
                ? 'Failed to update gateway. Please try again later.'
                : 'Failed to add gateway. Please try again later.',
              'error',
            ),
          );
        }
      }
    },
  });

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && gateway) {
      formik.resetForm({
        values: {
          name: gateway.name,
          userId: gateway.userId,
          objectId: gateway.objectId ?? '',
          model: (gateway.model as GatewayModel) || '',
          eui: gateway.serial,
        },
      });
    } else {
      formik.resetForm({ values: EMPTY });
    }
    setEuiRecognitionError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEdit, gateway?.id]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    gatewaysApi.listUsers().then((res) => {
      if (!cancelled) setUsers(res);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const userId = typeof formik.values.userId === 'number' ? formik.values.userId : undefined;
    gatewaysApi.listObjects(userId).then((res) => {
      if (!cancelled) setObjects(res);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isOpen, formik.values.userId]);

  const userOptions = useMemo<SelectOption<number>[]>(
    () =>
      [...users]
        .sort((a, b) => a.fullname.localeCompare(b.fullname))
        .map((u) => ({ value: u.id, label: u.fullname })),
    [users],
  );

  const objectOptions = useMemo<SelectOption<number>[]>(
    () =>
      [...objects]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((o) => ({ value: o.id, label: o.name })),
    [objects],
  );

  const modelOptions = useMemo<SelectOption<GatewayModel>[]>(
    () => GATEWAY_MODELS.map((m) => ({ value: m, label: m })),
    [],
  );

  const handleUserChange = (userId: number) => {
    const selected = objects.find((o) => o.id === formik.values.objectId);
    formik.setFieldValue('userId', userId);
    if (selected && selected.user_id !== userId) {
      formik.setFieldValue('objectId', '');
    }
  };

  const handleObjectChange = (objectId: number) => {
    formik.setFieldValue('objectId', objectId);
    if (typeof formik.values.userId !== 'number') {
      const owner = objects.find((o) => o.id === objectId);
      if (owner) {
        formik.setFieldValue('userId', owner.user_id);
      }
    }
  };

  const handleNameChange = (value: string) => {
    if (value.length <= 100) {
      formik.setFieldValue('name', value);
    }
  };

  const handleEuiChange = (value: string) => {
    const trimmed = value.slice(0, EUI_MAX);
    formik.setFieldValue('eui', trimmed);
    setEuiRecognitionError(null);
  };

  const handleScanClick = () => {
    setEuiRecognitionError(null);
    fileInputRef.current?.click();
  };

  const handleScanFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!GATEWAY_EUI_ALLOWED_TYPES.includes(file.type)) {
      setEuiRecognitionError('Unsupported file type. Allowed: JPEG, PNG, WEBP.');
      return;
    }
    if (file.size > GATEWAY_EUI_MAX_SIZE) {
      setEuiRecognitionError('File too large. Max size is 5 MB.');
      return;
    }

    setScanning(true);
    setEuiRecognitionError(null);
    try {
      const result = await gatewaysApi.recognizeEui(file);
      if (result.recognized && result.eui) {
        formik.setFieldValue('eui', result.eui.slice(0, EUI_MAX));
        formik.setFieldError('eui', undefined);
      } else {
        setEuiRecognitionError(result.message || 'Unable to recognize Gateway EUI. Please try again.');
      }
    } catch {
      setEuiRecognitionError('Unable to recognize Gateway EUI. Please try again.');
    } finally {
      setScanning(false);
    }
  };

  const showError = (field: keyof FormValues) =>
    !!(formik.touched[field] && formik.errors[field]);

  const euiTooltip = isEdit
    ? 'Cannot be changed after registration. To replace — delete and re-add'
    : 'Upload a close-up photo of the EUI label. Make sure the label is clearly visible and in focus';

  return (
    <Modal
      className={styles.gatewayModal}
      isVisible={isOpen}
      isClose
      isCloseBg
      title={isEdit ? 'Edit gateway' : 'Add new gateway'}
      onClose={onClose}
    >
      <form className={styles.gwForm} onSubmit={formik.handleSubmit} noValidate>
        <div className={styles.gwFormFieldWithHint}>
          <Field label="Gateway name" required invalid={showError('name')}>
            <input
              type="text"
              className={styles.gwInput}
              value={formik.values.name}
              placeholder="Gateway name"
              onBlur={() => formik.setFieldTouched('name', true)}
              onChange={(e) => handleNameChange(e.target.value)}
            />
          </Field>
          {showError('name') && (
            <p className={styles.gwFieldError}>{formik.errors.name}</p>
          )}
        </div>

        <div className={styles.gwFormFieldWithHint}>
          <SelectField
            label="User"
            required
            searchable
            searchPlaceholder="Search user..."
            value={formik.values.userId}
            options={userOptions}
            placeholder="Select user"
            invalid={showError('userId')}
            onChange={(v) => {
              handleUserChange(v);
              formik.setFieldTouched('userId', true);
            }}
          />
          {showError('userId') && (
            <p className={styles.gwFieldError}>{formik.errors.userId as string}</p>
          )}
        </div>

        <SelectField
          label="Object"
          searchable
          searchPlaceholder="Search object..."
          value={formik.values.objectId}
          options={objectOptions}
          placeholder="Select object"
          onChange={handleObjectChange}
          onClear={() => formik.setFieldValue('objectId', '')}
        />

        <div className={styles.gwFormFieldWithHint}>
          <SelectField
            label="Gateway model"
            required
            value={formik.values.model}
            options={modelOptions}
            placeholder="Select gateway model"
            disabled={isEdit}
            invalid={showError('model')}
            onChange={(v) => {
              formik.setFieldValue('model', v);
              formik.setFieldTouched('model', true);
            }}
          />
          {showError('model') && (
            <p className={styles.gwFieldError}>{formik.errors.model}</p>
          )}
        </div>

        <div className={styles.gwFormFieldWithHint}>
          <Tooltip text={euiTooltip}>
            <Field
              label="Gateway EUI (Extended unique identifier)"
              required
              disabled={isEdit}
              invalid={showError('eui')}
            >
              <input
                type="text"
                className={styles.gwInput}
                value={formik.values.eui}
                placeholder="Gateway EUI"
                disabled={isEdit}
                maxLength={EUI_MAX}
                onBlur={() => formik.setFieldTouched('eui', true)}
                onChange={(e) => handleEuiChange(e.target.value)}
              />
              <button
                type="button"
                className={styles.gwScanInline}
                aria-label="Scan EUI label"
                disabled={isEdit || scanning}
                onClick={(e) => {
                  e.stopPropagation();
                  handleScanClick();
                }}
              >
                <ScanIcon />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept={GATEWAY_EUI_ALLOWED_TYPES.join(',')}
                hidden
                onChange={handleScanFile}
              />
            </Field>
          </Tooltip>
          {euiRecognitionError ? (
            <p className={styles.gwFieldError}>{euiRecognitionError}</p>
          ) : showError('eui') ? (
            <p className={styles.gwFieldError}>{formik.errors.eui}</p>
          ) : (
            <p className={styles.gwFieldHint}>
              16-character hexadecimal identifier (0-9, A-F). Example: A840410000001234
            </p>
          )}
        </div>

        <SelectField
          label="Frequency band"
          required
          value={'EU868' as const}
          options={[{ value: 'EU868', label: 'EU868' }]}
          placeholder="EU868"
          disabled
          onChange={() => {}}
        />

        <div className={styles.gwFormNote}>
          Note: After adding the gateway, you'll receive a step-by-step configuration guide to
          complete the physical setup and network integration.
        </div>

        <div className={styles.gwFormActions}>
          <button
            type="button"
            className={classNames(styles.gwFormButton, styles.gwFormCancel)}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={classNames(styles.gwFormButton, styles.gwFormSubmit)}
          >
            {isEdit ? 'Update' : 'Add'}
          </button>
        </div>
      </form>
    </Modal>
  );
};
